import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Circle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

/**
 * OnboardingTracker
 * Reads /api/onboarding/status for the current user and renders a per-role checklist.
 * If everything is done (pct=100), renders nothing — keeps the dashboard clean.
 */
export default function OnboardingTracker() {
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("agf_onboarding_hidden") === "1"; } catch { return false; }
  });

  useEffect(() => {
    api.get("/onboarding/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  const dismiss = () => {
    try { localStorage.setItem("agf_onboarding_hidden", "1"); } catch { /* noop */ }
    setHidden(true);
  };

  if (!status || !status.steps?.length || status.pct === 100 || hidden) return null;

  const nextStep = status.steps.find((s) => !s.done);

  return (
    <div
      data-testid="onboarding-tracker"
      className="bg-white border border-stone-200 rounded-md p-6 sm:p-7"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">
            Onboarding · {status.role || "Member"}
          </div>
          <h3 className="mt-2 font-serif text-2xl text-stone-900">
            {nextStep ? `Next: ${nextStep.label}` : "Finish setting up your account."}
          </h3>
          <p className="mt-1 text-sm text-stone-600 font-sans">
            {status.done} of {status.total} steps complete — should take under 5 minutes.
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-3">
          <div className="w-32 h-2 rounded-full bg-stone-200 overflow-hidden">
            <div
              data-testid="onboarding-progress"
              className="h-full bg-emerald-700 transition-all"
              style={{ width: `${status.pct}%` }}
            />
          </div>
          <div className="font-serif text-lg text-emerald-800">{status.pct}%</div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={dismiss}
            data-testid="onboarding-dismiss"
            className="text-stone-500 hover:bg-stone-100"
          >
            Hide
          </Button>
        </div>
      </div>

      <ul className="mt-5 divide-y divide-stone-100">
        {status.steps.map((s) => (
          <li
            key={s.key}
            data-testid={`onboarding-step-${s.key}`}
            className="py-3 flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-3">
              {s.done ? (
                <Check size={18} className="text-emerald-700" strokeWidth={2.5} />
              ) : (
                <Circle size={18} className="text-stone-400" />
              )}
              <span
                className={
                  s.done
                    ? "font-sans text-sm text-stone-500 line-through"
                    : "font-sans text-sm text-stone-800"
                }
              >
                {s.label}
              </span>
            </div>
            {!s.done && (
              <Link to={s.cta}>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-emerald-800 hover:bg-emerald-50"
                  data-testid={`onboarding-cta-${s.key}`}
                >
                  Do this <ArrowRight size={14} className="ml-1.5" />
                </Button>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
